/**
 * 에이전트 — 화면 기기에 까는 프로그램. 등록 · 기기에서 쓰기 · 업데이트.
 *
 * **설치와 새 버전 배포는 적지 않는다.** 그 일은 공급사가 정비 모드 메뉴(에이전트 화면)에서
 * 하고, 정비 모드는 사용자에게 보이는 자리가 아니다. 사용자의 몫은 설치된 기기를 등록하고,
 * 먼저 받아 볼 화면을 정하고, 현장에서 기기를 다루는 데까지다.
 *
 * 콘솔을 쓰는 사람과 기기 앞에 서는 사람이 다를 때가 많다. 그래서 기기 화면에 뜨는
 * 글자를 콘솔 단추와 똑같이 `[[…]]` 로 적는다 — 전화로 "그 화면에 뭐라고 떠요?" 를
 * 주고받을 때 같은 말을 쓰게 하려는 것이다.
 */
import { MonitorCheck, RefreshCw, Tv } from 'lucide-react';
import type { ManualChapter } from '../types.js';

export const AGENT_INSTALL: ManualChapter = {
  slug: 'agent-install',
  title: '화면 등록하기',
  summary: '에이전트가 설치된 PC·TV 박스를 기기 화면의 6자리 코드로 콘솔에 등록합니다.',
  icon: MonitorCheck,
  sections: [
    {
      id: 'devices',
      title: '에이전트를 설치할 수 있는 기기',
      blocks: [
        {
          kind: 'text',
          text: '**에이전트**는 화면에 연결된 기기에 설치하는 재생 프로그램입니다. 콘솔에서 정한 편성을 받아 전체 화면으로 재생하고, 기기가 켜질 때 자동으로 실행되며, 인터넷이 끊겨도 내려받아 둔 콘텐츠를 계속 재생합니다.',
        },
        {
          kind: 'table',
          header: ['기기', '지원', '알아둘 점'],
          rows: [
            ['Windows PC', '지원', '64비트 Windows 가 필요합니다.'],
            ['안드로이드 TV 박스·태블릿', '지원', '안드로이드 5.1 이상.'],
            ['웹 브라우저', '제한적', '설치 없이 브라우저로 재생할 수 있지만 자동 실행·자동 업데이트·정비 모드가 없습니다.'],
            ['macOS · Linux', '준비 중', '아직 설치 파일이 제공되지 않습니다.'],
          ],
        },
        {
          kind: 'callout',
          tone: 'note',
          title: '직접 설치할 수도 있습니다',
          text: '콘솔 [[에이전트 설치]] 메뉴([[화면]] 메뉴 바로 아래)에서 기기 종류에 맞는 설치 파일을 바로 내려받을 수 있습니다. 안드로이드는 기기 브라우저로 QR 을 찍거나, 주소창에 **콘솔 주소 뒤에 /apk** 를 입력하면 안내와 [[설치 파일 내려받기]] 가 있는 화면이 열립니다 — 입력할 코드나 토큰은 없습니다. 설치할 기기가 많으면 공급사(설치 담당자)에게 요청할 수 있습니다.',
        },
        {
          kind: 'steps',
          title: '직접 설치하기 (안드로이드)',
          steps: [
            { title: '콘솔에서 [[에이전트 설치]] 를 엽니다.', body: '왼쪽 메뉴의 [[화면]] 바로 아래에 있습니다. 화면을 등록할 수 있는 권한이 있어야 보입니다.' },
            { title: '기기로 QR 을 찍거나, 기기 브라우저에 주소를 입력합니다.', body: '카메라가 없는 TV 박스는 주소창에 콘솔 주소 뒤에 **/apk** 를 입력합니다. 리모컨으로도 입력할 수 있는 길이입니다.' },
            { title: '[[설치 파일 내려받기]] 를 누르고, 내려받은 파일을 엽니다.', body: '“알 수 없는 출처” 허용을 물으면 허용합니다 — 내려받는 데 쓴 앱에 한 번만 허용하면 됩니다. “앱이 설치되지 않음” 이 표시되면 대부분 기기의 칩이 달라서이며, 그 화면에서 다른 설치 파일을 고를 수 있습니다.' },
            { title: '설치한 뒤 앱을 열면 6자리 코드가 표시됩니다.', body: '서버 주소가 이미 들어 있어 따로 묻지 않습니다. 그 코드를 [[화면 추가]] 에 입력하면 등록이 끝납니다.' },
          ],
        },
        {
          kind: 'callout',
          tone: 'warn',
          title: '윈도우 PC 는 주소를 한 번 묻습니다',
          text: '윈도우 설치 파일에는 서버 주소가 들어 있지 않습니다. 설치한 뒤 처음 열면 주소를 묻습니다. [[에이전트 설치]] 화면에 입력할 주소가 그대로 적혀 있으니 복사해 붙여 넣으세요. 설치 중 **Windows 보호 경고** 가 표시되면 [[추가 정보]] ▸ [[실행]] 으로 진행합니다.',
        },
        {
          kind: 'callout',
          tone: 'tip',
          title: '업데이트는 기기가 자동으로 내려받습니다',
          text: '한 번 설치한 뒤에는 새 버전을 다시 내려받아 설치할 필요가 없습니다. 기기가 자동으로 내려받아 갱신합니다. 갱신되지 않는 화면이 있으면 [[화면]] 목록의 버전 열에서 확인하고 공급사에 알려 주세요.',
        },
        {
          kind: 'callout',
          tone: 'tip',
          title: '기기 절전을 꺼 두세요',
          text: '기기의 절전·화면 끄기를 “안 함” 으로 설정하세요. 화면이 꺼지면 에이전트가 재생 중이어도 아무것도 보이지 않습니다.',
        },
      ],
    },
    {
      id: 'flow',
      title: '전체 흐름',
      blocks: [
        { kind: 'figure', figure: 'install-flow', caption: '공급사가 에이전트 설치 → 기기 화면의 6자리 코드 확인 → 콘솔 화면 추가에 입력' },
        {
          kind: 'text',
          text: '설치가 끝난 기기 화면에는 **6자리 페어링 코드**(예: H7KP3W)가 표시됩니다. 이 기기를 콘솔에 연결하는 코드이며, 콘솔 [[화면 추가]] 에 입력합니다.',
        },
      ],
    },
    {
      id: 'pair',
      title: '콘솔에 등록하기 (페어링)',
      blocks: [
        {
          kind: 'steps',
          title: '따라 하기',
          steps: [
            { title: '기기 화면의 6자리 코드를 확인합니다.', body: '“관리자 콘솔의 [화면 추가]에 이 코드를 입력하세요.” 라는 안내가 함께 보입니다.' },
            { title: '콘솔 [[화면]] 메뉴에서 [[화면 추가]] 를 누릅니다.' },
            { title: '창 맨 위 “등록 대기 중인 화면” 에서 그 기기를 누르거나 등록 코드를 넣고, 화면 이름·위치를 정해 [[등록]] 을 누릅니다.' },
            { title: '기기 화면이 “등록되었습니다” 로 바뀌면 등록이 끝난 것입니다.', body: '이어서 화면에 편성을 배정하세요. 배정하기 전에는 기기에 “편성이 배정되지 않았습니다” 가 표시됩니다.' },
          ],
        },
        {
          kind: 'callout',
          tone: 'note',
          title: '코드는 15분마다 바뀝니다',
          text: '적어 온 코드에 “만료되었습니다” 가 표시되면 기기 화면에 지금 표시된 코드를 다시 확인하세요.',
        },
      ],
    },
    {
      id: 'faq',
      title: '등록되지 않을 때',
      blocks: [
        {
          kind: 'faq',
          items: [
            {
              q: '기기에 코드 대신 “서버 주소” 를 입력하는 칸이 표시됩니다.',
              a: '에이전트가 연결할 서버 주소를 모르는 상태입니다. 공급사(설치 담당자)에게 서버 주소를 받아 입력하고 [[연결]] 을 누르세요.',
            },
            {
              q: '기기에 에이전트가 없거나, 새 기기를 더 설치하려고 합니다.',
              a: '에이전트 설치는 공급사가 합니다. 기기 종류(Windows PC · 안드로이드)와 대수를 알려 설치를 요청하세요.',
            },
          ],
        },
      ],
    },
  ],
};

export const AGENT_DEVICE: ManualChapter = {
  slug: 'agent-device',
  title: '기기 화면과 정비 모드',
  summary: '기기 화면에 표시되는 안내의 뜻, 인터넷이 끊겼을 때의 동작, 현장에서 정비 모드를 사용하는 방법.',
  icon: Tv,
  sections: [
    {
      id: 'messages',
      title: '기기 화면에 표시되는 안내',
      blocks: [
        {
          kind: 'table',
          header: ['기기에 보이는 것', '뜻', '할 일'],
          rows: [
            ['6자리 코드 + “[화면 추가]에 이 코드를 입력하세요”', '아직 콘솔에 등록되지 않았습니다', '콘솔 [[화면 추가]] 에 코드 입력'],
            ['편성이 배정되지 않았습니다', '등록은 되었지만 재생할 편성이 정해지지 않았습니다', '화면 상세 “편성 배정” 에서 고르기'],
            ['재생할 콘텐츠가 없습니다', '배정한 플레이리스트가 비었거나 콘텐츠가 모두 보관되었습니다', '플레이리스트에 콘텐츠 담기'],
            ['콘텐츠 적용 중 · NN%', '새 콘텐츠를 내려받고 있습니다', '기다리기 — 끝나면 5초 뒤 자동으로 재생(긴급 송출은 곧바로)'],
            ['서버에 연결할 수 없습니다', '인터넷이 끊겼고 내려받아 둔 콘텐츠도 없습니다', '기기 네트워크(와이파이·랜선) 확인'],
            ['정비 모드 · 재생·증빙 멈춤', '현장에서 정비 모드를 열어 둔 채입니다', '오른쪽 위 **×** 누르기'],
            ['완전히 검은 화면', '프로그램이 멈췄거나 기기가 영상 형식을 처리하지 못했습니다', '기기 전원을 껐다 켜기 — 반복되면 문제 해결 장 참고'],
          ],
        },
      ],
    },
    {
      id: 'applying',
      title: '새 콘텐츠를 내려받는 동안',
      blocks: [
        {
          kind: 'text',
          text: '편성을 바꾸면 기기는 새 콘텐츠를 모두 내려받을 때까지 “**콘텐츠 적용 중**” 과 진행률(%)을 표시합니다. 내려받기가 끝나면 “적용 완료” 와 함께 5부터 거꾸로 센 뒤 새 편성을 재생합니다. 용량이 큰 영상은 몇 분 걸릴 수 있습니다.',
        },
        {
          kind: 'callout',
          tone: 'note',
          title: '내려받지 못하면 이전 편성을 계속 재생합니다',
          text: '파일을 하나도 내려받지 못하면 적용 화면을 닫고 원래 재생하던 편성을 이어서 재생합니다. 화면이 검게 비는 것을 막기 위해서입니다.',
        },
      ],
    },
    {
      id: 'offline',
      title: '인터넷이 끊기면',
      blocks: [
        {
          kind: 'text',
          text: '에이전트는 **마지막으로 내려받은 편성과 콘텐츠를 계속** 재생합니다. 인터넷이 복구되면 자동으로 서버와 다시 맞춥니다. 끊긴 동안의 재생 기록도 다시 연결되면 서버로 올라갑니다. 콘솔에는 그 화면이 “오프라인” 으로 표시됩니다.',
        },
        {
          kind: 'callout',
          tone: 'warn',
          title: '오래된 안드로이드 5.1 TV 박스는 예외가 있습니다',
          text: '5.1 기기는 내려받아 둔 콘텐츠를 **재부팅하면 잃습니다.** 인터넷이 끊긴 채 전원이 꺼졌다 켜지면 다시 연결될 때까지 재생할 콘텐츠가 없습니다.',
        },
      ],
    },
    {
      id: 'maintenance',
      title: '정비 모드',
      blocks: [
        {
          kind: 'text',
          text: '현장에서 기기 상태를 확인하거나, 업데이트를 직접 내려받거나, 서버 주소를 바꿀 때 사용하는 화면입니다. 열려 있는 동안에는 **재생이 멈춥니다.**',
        },
        { kind: 'figure', figure: 'maintenance-gesture', caption: '화면을 두 번 누르고 PIN 을 입력하면 정비 모드가 전체 화면으로 열립니다' },
        {
          kind: 'steps',
          title: '정비 모드 열고 닫기',
          steps: [
            { title: '화면 아무 곳이나 빠르게 두 번 누릅니다.', body: 'Windows 는 마우스로 더블클릭하거나 키보드의 F10 을 눌러도 됩니다. 리모컨만 있는 TV 기기(Google TV 스틱 등)는 리모컨의 가운데 **확인 버튼을 3초 동안 누르고 있습니다.** 두 번 누르는 사이가 0.5초를 넘으면 한 번씩 누른 것으로 처리됩니다.' },
            { title: '표시되는 숫자판에 PIN 을 입력하고 [[확인]] 을 누릅니다.', body: 'PIN 은 콘솔 화면 상세에서 톱니바퀴 → “화면 설정” 창의 [[정비 PIN]] 메뉴에서 정하거나, 현장에서 정비 모드 [[서버 연결]] 의 [[PIN 바꾸기]] 로 정합니다(아래). 아무 데서도 정하지 않은 기기는 **0000** 입니다. 5번 연속 틀리면 30초 동안 입력할 수 없고, 30초 동안 아무것도 누르지 않으면 숫자판이 닫힙니다.' },
            { title: '정비 모드가 전체 화면으로 열립니다. 왼쪽 메뉴에서 볼 항목을 고릅니다.', body: '[[개요]] · [[재생]] · [[서버 연결]] · [[네트워크]] · [[소프트웨어]] · [[전원·시작]] 여섯 가지이며, 고른 항목의 내용이 오른쪽에 나옵니다. 아래 설명을 참고하세요.' },
            { title: '끝나면 반드시 오른쪽 위 **×** 를 누릅니다.', body: '정비 모드가 닫히고 재생이 다시 시작됩니다. Windows 는 F10 을 다시 눌러도 됩니다.' },
          ],
        },
        {
          kind: 'actions',
          title: '정비 모드의 메뉴와 단추',
          items: [
            { label: '개요', text: '왼쪽 메뉴의 첫 항목입니다. 이 기기가 콘솔의 어느 화면인지 — 이름 · **디바이스 ID** · 버전이 나옵니다. 정비 모드를 열면 이 항목이 먼저 열려 있습니다. 디바이스 ID 는 콘솔 화면 상세 “기본 정보” 의 값과 같으며, [[화면]] 목록 검색칸에 일부(뒤 6자리 등)만 입력해도 그 화면을 찾을 수 있습니다.' },
            { label: '지금 확인', text: '[[소프트웨어]] 메뉴에 있습니다. 새 에이전트 버전이 있는지 바로 확인합니다.' },
            { label: '지금 업데이트', text: '[[소프트웨어]] 메뉴에 있습니다. 새 버전을 내려받아 설치합니다. 안드로이드에서 설치 확인 창이 표시되면 “설치” 를 누르세요.' },
            { label: '자동 업데이트', text: '[[소프트웨어]] 메뉴에 있습니다. 끄면 이 기기는 [[지금 업데이트]] 를 누를 때만 갱신합니다. 특별한 이유가 없으면 켜 두세요. 콘솔에서 정해 두었으면 스위치 아래에 “콘솔에서 정함” 이 나오고 여기서는 바꿀 수 없습니다. **이 기기가 확인 창 없이 설치할 수 없으면**(기기 소유자가 아니고 루트도 없는 안드로이드 11 이하, 루트 없는 안드로이드 5.x, ‘출처를 알 수 없는 앱 설치’ 가 꺼진 안드로이드 12 이상) 스위치가 꺼진 채 눌리지 않고, 그 아래에 “지원되지 않는 기능입니다.” 와 원인이 나옵니다 — 이때도 [[지금 업데이트]] 로는 설치할 수 있습니다. 안드로이드 12 이상은 기기 소유자가 아니어도(Google TV 스틱 등) 기기 설정 ▸ 앱 ▸ SignageOne 의 ‘출처를 알 수 없는 앱 설치’ 만 켜 두면 확인 창 없이 스스로 업데이트합니다(에이전트 1.1.2 부터 — 1.1.2 까지는 한 번 [[지금 업데이트]] 로 올립니다). 원인이 **기기**면 그 기기의 준비(기기 소유자 등록 · 접근성 서비스 · 루트 · 알 수 없는 앱 설치 허용)가 안 된 것이고, **OS** 면 그 안드로이드 버전에는 길이 없는 것입니다(새 버전이면 같은 준비로 됩니다).' },
            { label: '부팅 시 자동 실행', text: '[[전원·시작]] 메뉴에 있습니다. 기기를 켜면 에이전트가 저절로 실행됩니다. **기본은 켜짐**이며, 특별한 이유가 없으면 그대로 두세요 — 꺼 두면 정전이 난 뒤 사람이 현장에 가서 켜야 합니다. 끄면 키오스크로 고정된 화면은 그 고정(홈 화면 잠금)도 함께 풀립니다. 자동 업데이트로 새 버전이 깔린 뒤에는 이 설정과 상관없이 다시 실행됩니다 — 갱신 때문에 화면이 꺼진 채 남지 않게 하기 위해서입니다. 콘솔에서 정해 두었으면 자동 업데이트와 같이 잠깁니다. 안드로이드 10 이상에서 기기 소유자가 아니고 자동 실행용 접근성 서비스도 꺼져 있으면 기기를 켜도 뜰 수 없어, 스위치가 눌리지 않고 “지원되지 않는 기능입니다.” 와 원인이 나옵니다 — 기기 설정 ▸ 접근성에서 ‘SignageOne 자동 실행’ 을 켜고 돌아오면 바로 풀립니다.' },
            { label: '네트워크', text: '왼쪽 메뉴의 한 항목입니다. 이 기기가 지금 어떤 방식으로(유선 · Wi-Fi) 어떤 IP 로 망에 붙어 있는지 보여 줍니다 — 게이트웨이 · DNS 도 함께 나오므로 “서버에 연결할 수 없습니다” 가 뜰 때 가장 먼저 볼 화면입니다. **MAC 주소**(공유기 DHCP 예약 · 방화벽 허용에 쓰는 기기 고유 주소)도 여기 **하드웨어** 칸에 나옵니다([[개요]] 에는 두지 않습니다) — 안드로이드 11 이상은 OS 가 앱에 주지 않아 MAC 줄이 “사용 불가 · OS 제한”(값은 “확인 불가”)으로 나오니 기기의 설정 › 네트워크에서 확인하세요(기기 소유자로 설정한 기기는 Wi-Fi MAC 이 읽힙니다). Wi-Fi 면 몇 GHz 로 붙었는지(대역 · 채널)와 신호 세기도 나오고, 안드로이드 5.x 기기는 주변에 보이는 Wi-Fi 망 수를 2.4GHz · 5GHz 로 나눠 보여 줍니다. **5GHz 공유기 가까이인데도 5GHz 가 “없음” 이면 그 기기는 5GHz 를 받지 못합니다** — 2.4GHz 망에 붙이거나 유선으로 연결하세요. **에이전트가 Wi-Fi 나 고정 IP 를 직접 바꾸지는 못합니다**(OS 가 앱에 허용하지 않습니다). 바꾸려면 [[네트워크 설정 열기]] 로 기기의 설정 화면을 열어 거기서 고친 뒤 돌아오세요 — 바뀐 연결이 곧바로(새 망에 붙는 중이면 붙는 대로 몇 초 안에) 이 화면에 나옵니다. [[다시 읽기]] 는 지금 바로 한 번 더 읽고 싶을 때 누릅니다.' },
            { label: 'PIN 바꾸기', text: '[[서버 연결]] 메뉴의 **정비 PIN** 칸에 있습니다. 바로 위 “지금 쓰는 PIN” 줄이 정비 모드를 여는 PIN 이 어디서 왔는지 알려 줍니다 — **기본값 0000** · **이 기기에서 정함** · **콘솔에서 정함**(PIN 숫자는 나오지 않습니다). 누르면 숫자판이 뜹니다. 새 PIN(숫자 4~8자리)을 넣고 [[확인]], 같은 PIN 을 한 번 더 넣고 [[확인]] 을 누르면 바뀌고, 줄 아래에 “바꿨습니다” 가 나옵니다 — 다음부터 새 PIN 으로 들어옵니다. 두 번이 다르면 처음부터 다시 넣습니다. 바꾼 PIN 은 이 기기에만 저장되어 서버와 끊겨도, 기기를 껐다 켜도 그대로입니다. **콘솔에서 PIN 을 정해 둔 기기는 그 PIN 이 우선이라** 이 줄이 “콘솔에서 정함” 으로 잠깁니다 — 콘솔에서 바꾸거나 [[콘솔 PIN 해제]] 를 누르면 여기서 바꿀 수 있습니다. 에이전트 0.7.20 이상에서 됩니다(그보다 옛 판은 “사용 불가 · 이전 판”).' },
            { label: '적용', text: '[[서버 연결]] 메뉴에 있습니다. 입력한 서버 주소가 쓸 수 있는 곳인지 **먼저 확인한 뒤** 바꿉니다. 그 서버에 이 화면이 이미 등록되어 있으면(서버 주소만 바뀐 경우) **등록을 그대로 둔 채** 주소만 옮기고, 등록되어 있지 않으면 한 번 더 물어본 뒤 새 등록 코드를 띄웁니다. 지금 쓰는 주소를 그대로 두고 눌러 연결 확인에만 쓸 수도 있습니다.' },
            { label: '기기 재부팅 · 전원 종료', text: '[[전원·시작]] 메뉴의 **전원** 칸에 있습니다. 둘 다 한 번 더 물어본 뒤 실행합니다. [[기기 재부팅]] 은 기기를 다시 시작합니다 — 자동 실행이 켜져 있으면 1~2분 뒤 재생으로 돌아옵니다. [[전원 종료]] 는 기기를 끕니다 — 다시 켜려면 현장에서 전원을 넣어야 합니다. **안드로이드 기기는 기기가 허락해야 됩니다** — 루트 권한이 있는 기기는 둘 다, 안드로이드 7.0 이상에서 키오스크로 설치한 기기는 재부팅만 됩니다. 안 되는 단추는 눌리지 않고, 그 아래에 “지원되지 않는 기능입니다.” 와 원인(기기 또는 OS)이 나옵니다. Windows 는 둘 다 됩니다(다른 프로그램은 저장하지 않고 닫힙니다). 재부팅은 콘솔에서도 할 수 있습니다(아래 “콘솔에서 원격으로 정하기”).' },
            { label: '앱 종료', text: '[[전원·시작]] 메뉴 맨 아래 **전원** 칸의 [[앱 종료]] 입니다(예전 판의 왼쪽 아래 전원 아이콘은 같은 단추가 두 곳에 있던 것이라 없앴습니다). 누르면 한 번 더 물어보고, 확인하면 에이전트를 **완전히** 끝냅니다 — 화면에 아무것도 표시되지 않고, 감시 기능도 함께 멈춥니다. 사람이 다시 켜거나 기기를 재부팅할 때까지 꺼진 채로 있습니다(재부팅하면 저절로 다시 시작합니다).' },
            { label: '정비 모드 닫고 재생으로 돌아가기', text: '오른쪽 위의 **×** 단추입니다. 정비 모드를 닫고 재생을 다시 시작하며, 어느 메뉴를 보고 있든 늘 같은 자리에 있습니다.' },
          ],
        },
        {
          kind: 'text',
          text: '**이 기기에서 쓸 수 없는 줄은 줄 전체가 표시됩니다.** 기기·OS 의 제한이나 에이전트 앱 없이 연 화면이라서 지원되지 않거나 값을 얻을 수 없는 항목(자동 업데이트 · 부팅 시 자동 실행 · 기기 재부팅 · 전원 종료 · MAC 등)은 이름과 값이 함께 흐려지고, 줄에 빗금 바탕과 테두리가 깔리며, 이름 옆에 “사용 불가 · OS 제한” · “사용 불가 · 기기 제한” · “사용 불가 · 앱 없음” 표가 붙습니다. 고장이 아니라 그 기기에서는 원래 안 되는 항목이라는 뜻입니다 — 잠깐 흐려졌다가 돌아오는 줄(확인하는 중 · 실행하는 중)이나 “콘솔에서 정함” 으로 잠긴 스위치와 다릅니다. 그 줄로 가면 아래 설명 칸과 줄 아래 글이 원인과 할 수 있는 일을 알려 줍니다. [[개요]] 의 칸도 그 기능을 쓸 수 없으면 같은 모양의 표(“사용 불가”)로 나옵니다.',
        },
        {
          kind: 'callout',
          tone: 'warn',
          title: '정비 모드는 원격으로 닫을 수 없습니다',
          text: '오른쪽 위 **×** 를 누르지 않고 현장을 떠나면 화면은 **정비 모드에 멈춘 채** 남고, 자동 업데이트도 건너뜁니다. 콘솔의 화면 목록·화면 상세·모니터링에 “정비 모드” 표시가 붙지만 콘솔에서는 해제할 수 없습니다. 현장 작업의 마지막 동작은 반드시 **×** 입니다.',
        },
        {
          kind: 'callout',
          tone: 'warn',
          title: '공공장소에 설치한 기기는 PIN 을 바꿔 두세요',
          text: '화면을 두 번 누르는 동작은 지나가는 누구나 할 수 있고, PIN 을 정하지 않은 기기는 짐작하기 쉬운 **0000** 으로 열립니다. 사람이 오가는 곳에 설치한 기기는 콘솔에서 PIN 을 바꾸거나, 설치하는 자리에서 정비 모드 [[서버 연결]] ▸ [[PIN 바꾸기]] 로 바꿔 두세요. Windows PC 는 키보드를 분리해 두는 편이 안전합니다.',
        },
        {
          kind: 'steps',
          title: '콘솔에서 PIN 바꾸기',
          steps: [
            { title: '한 대: [[화면]] 에서 그 화면을 열고, 오른쪽 위 **톱니바퀴**로 “화면 설정” 창을 연 뒤 [[정비 PIN]] 메뉴에서 새 PIN 을 두 번 입력하고 [[PIN 저장]] 을 누릅니다.', body: '숫자 4~8자리입니다. [[콘솔 PIN 해제]] 를 누르면 기기에 설정된 PIN(없으면 0000)으로 돌아갑니다.' },
            { title: '여러 대: [[화면]] 목록에서 체크하고 막대의 [[기타 일괄 변경]] ▸ [[정비 PIN…]] 을 누릅니다.', body: '선택한 화면 모두에 같은 PIN 이 설정됩니다.' },
            { title: '보통 30초 안에 기기에 적용됩니다.', body: '꺼져 있거나 인터넷이 끊긴 기기는 다시 연결될 때 내려받으며, 그때까지는 이전 PIN 으로 열립니다.' },
          ],
        },
        {
          kind: 'steps',
          title: '콘솔에서 원격으로 정하기 — 자동 업데이트 · 부팅 시 자동 실행 · 재부팅 · 앱 종료 · 전원 종료',
          steps: [
            { title: '[[화면]] 에서 그 화면을 열고, 오른쪽 위 **톱니바퀴**로 “화면 설정” 창을 엽니다.', body: '왼쪽 메뉴의 [[소프트웨어]] 에 자동 업데이트가, [[전원·시작]] 에 부팅 시 자동 실행과 원격 명령이 있습니다 — 기기 정비 화면의 메뉴 이름과 같습니다. ‘화면 관리’ 권한이 있는 계정에만 보입니다. 에이전트 0.7.6 이상이 이 설정을 따르며, 그보다 옛 에이전트면 그 사실이 적힙니다.' },
            { title: '[[자동 업데이트]] · [[부팅 시 자동 실행]] 에서 **켜기 · 끄기 · 기기에 맡김** 중 하나를 고릅니다.', body: '고르는 즉시 저장되고 보통 30초 안에 기기에 적용됩니다. 꺼져 있는 기기는 다시 연결될 때 받습니다. 켜기·끄기로 정해 두면 기기 정비 화면의 그 스위치는 “콘솔에서 정함” 으로 잠기고, [[기기에 맡김]] 으로 되돌리면 다시 현장에서 바꿀 수 있습니다. 선택 아래 “현재 기기” 가 기기가 알려 온 실제 값입니다.' },
            { title: '[[원격 명령]] 의 [[기기 재부팅]], 또는 그 옆 [[…]] 의 [[앱 종료]] · [[전원 종료]] 를 누르고 확인합니다.', body: '켜져 있는 화면에만 보낼 수 있습니다. 기기는 30초 안에 받아 실행합니다. **재부팅**은 기기가 허락해야 하고(안드로이드는 루트 또는 7.0 이상 키오스크 설치), 자동 실행이 켜져 있으면 1~2분 뒤 재생으로 돌아옵니다. **앱 종료 · 전원 종료는 콘솔에서 다시 켤 수 없습니다** — 앱 종료 뒤에는 현장에서 앱을 다시 켜거나 기기를 껐다 켜야 하고, 전원 종료 뒤에는 현장에서 전원을 넣어야 합니다. 전원 종료는 안드로이드에서 루트가 있는 기기만 됩니다. 앱 종료·전원 종료는 에이전트 0.7.7 이상, 재부팅은 0.7.6 이상이 알아듣습니다. 안 되는 단추는 눌리지 않고 마우스를 올리면 이유가 나옵니다. 현장에서 정비 모드를 열어 둔 동안에는 실행하지 않고 “현장에서 정비 중이라 …하지 않았습니다” 로 알립니다.' },
          ],
        },
        {
          kind: 'callout',
          tone: 'note',
          title: '원격으로 하지 않는 것',
          text: '**서버 주소** 는 잘못 바꾸면 화면이 이 콘솔을 떠나 다시 붙잡을 수 없고, **네트워크(Wi-Fi · 고정 IP)** 는 에이전트가 바꾸지 못합니다. 이 둘은 현장 정비 화면에서만 합니다.',
        },
        {
          kind: 'callout',
          tone: 'note',
          title: '정해 둔 PIN 은 다시 볼 수 없습니다',
          text: 'PIN 은 서버에도 알아볼 수 없는 형태로만 저장됩니다. 잊었다면 새로 설정하세요. [[콘솔 PIN 해제]] 를 누르면 콘솔 PIN 이 지워지고 기기에 설정된 PIN(없으면 0000)으로 돌아갑니다. 화면 관리 권한이 있는 계정만 바꿀 수 있습니다.',
        },
      ],
    },
  ],
};

export const AGENT_UPDATE: ManualChapter = {
  slug: 'agent-update',
  title: '에이전트 업데이트',
  summary: '새 버전이 화면에 적용되는 방식, 먼저 받아 볼 화면을 정하는 방법, 업데이트되지 않을 때 확인할 항목.',
  icon: RefreshCw,
  sections: [
    {
      id: 'auto',
      title: '업데이트는 자동으로 적용됩니다',
      blocks: [
        {
          kind: 'text',
          text: '공급사가 새 버전을 배포하면, 켜져 있는 화면은 **대부분 1~2분 안에**(설치 시간대를 정해 두었으면 그 시간대에) 자동으로 내려받아 설치하고 다시 시작합니다. 꺼져 있던 화면은 켜진 뒤에 확인해 내려받습니다. 현장에서 따로 할 일은 없습니다.',
        },
        {
          kind: 'callout',
          tone: 'note',
          title: '‘설치 예약’ 은 설치 시간대를 기다리는 중입니다 (0.7.17 부터)',
          text: '공급사가 설치 시간대(예: 새벽 2시~5시)를 정해 두면, 영업 중인 화면이 다시 시작하며 깜빡이지 않도록 그 시간대에만 새 버전을 받아 설치합니다. 그 전까지 콘솔 [[화면]] 목록의 버전 칸에는 ‘새 버전 설치 예약’ 이 보입니다. 시간대가 열리면 화면마다 조금씩(최대 30분) 늦춰 받아 회선을 한꺼번에 쓰지 않습니다. 당장 올려야 하면 현장에서 정비 모드의 [[지금 업데이트]] 를 누르세요 — 시간대와 상관없이 설치됩니다.',
        },
        {
          kind: 'callout',
          tone: 'note',
          title: '설치 확인을 요구하는 안드로이드가 있습니다',
          text: '안드로이드는 설치할 때 “설치할까요?” 창을 사람이 눌러야 하는 경우가 있습니다. 그 창이 재생 화면을 덮지 않도록 0.6.4 부터는 자동으로 설치하지 않고, 콘솔에 ‘확인 대기’ 로 알립니다. 이때는 현장에서 기기의 정비 모드를 열고 [[지금 업데이트]] 를 누르세요. 기기 소유자(device owner)로 설정하면 대부분 확인 창 없이 설치되지만, 소유자로 설정해도 확인을 요구하는 기기가 있습니다(안드로이드 5.x 등). **루트(su)가 있는 기기는 0.7.7 부터 확인 창 없이 설치합니다** — 납품 TV 박스처럼 펌웨어에 루트가 있으면 0.7.7 을 한 번 현장에서 올린 뒤로는 저절로 올라갑니다. 루트 설치가 실패한 버전은 다시 시도하지 않고 ‘확인 대기’ 로 알립니다(다음 버전에서 다시 시도). 자세한 내용은 아래 “업데이트되지 않을 때” 를 참고하세요.',
        },
        {
          kind: 'callout',
          tone: 'tip',
          title: '한 번 막힌 기기는 같은 버전을 다시 내려받지 않습니다 (0.6.5 부터)',
          text: '확인을 요구하는 기기가 한 시간마다 같은 파일을 다시 내려받으면 매장 회선만 소모합니다. 그래서 한 번 막히면 그 사실을 기기에 기록해 두고, 다음부터는 **내려받지 않고 ‘확인 대기’ 만 알립니다.** 새 버전이 나오면 한 번 더 시도합니다 — 그 사이 기기 소유자로 등록했을 수 있기 때문입니다. 현장에서 정비 모드의 [[지금 업데이트]] 로 설치하면 이 표시는 지워집니다.',
        },
        {
          kind: 'callout',
          tone: 'note',
          title: '‘확인 대기’ 는 기기를 껐다 켜도 남습니다 (0.6.8 부터)',
          text: '밤마다 기기를 끄는 현장에서는, 예전에 재시작한 화면이 한동안 ‘업데이트를 묻지 않는 화면’ 처럼 보였습니다. 지금은 확인을 기다리던 상태가 그대로 남아 콘솔에 계속 ‘확인 대기’ 로 표시됩니다. 현장에 사람을 보낼지 판단할 수 있습니다.',
        },
        {
          kind: 'callout',
          tone: 'tip',
          title: '사람이 눌러 설치한 뒤에는 재생 화면이 자동으로 돌아옵니다 (0.6.6·0.6.8)',
          text: '시스템의 “설치되었습니다” 창이 재생 화면을 덮은 채 남지 않도록, 설치가 끝나면 기기가 재생 화면을 다시 앞으로 불러옵니다(확인 창 앞에 사람이 오래 서 있어도 **약 2분 30초까지** 기다립니다). 정비 모드를 열어 둔 동안이나 [[앱 종료]] 를 누른 뒤에는 동작하지 않습니다 — 현장 점검을 방해하지 않기 위해서입니다. 다만 **안드로이드 10 이상이면서 기기 소유자가 아닌 기기**에서는 OS 가 이 동작을 막습니다. 그런 기기는 기기 소유자로 등록해야 하고, 등록하면 처음부터 확인 창 없이 설치됩니다.',
        },
      ],
    },
    {
      id: 'channels',
      title: '카나리와 안정 — 일부 화면 먼저, 이어서 전체',
      blocks: [
        { kind: 'figure', figure: 'rollout', caption: '새 버전은 카나리 화면 몇 대가 먼저 받고, 문제가 없으면 안정 채널로 전체에 배포됩니다' },
        {
          kind: 'table',
          header: ['채널', '받는 화면', '이럴 때'],
          rows: [
            ['카나리', '채널을 “카나리” 로 지정한 몇 대', '새 버전을 실제 현장에서 먼저 시험할 때'],
            ['안정', '카나리로 지정하지 않은 **나머지 전부**', '카나리에서 문제가 없음을 확인한 뒤'],
          ],
        },
        {
          kind: 'callout',
          tone: 'warn',
          title: '한 번 올라간 버전은 되돌릴 수 없습니다',
          text: '화면은 낮은 버전으로 되돌아가지 않습니다. 새 버전에 문제가 있으면 공급사가 문제를 고친 **더 높은 버전**을 다시 배포해야 합니다. 카나리 화면을 정해 두고 먼저 확인하는 편이 안전합니다.',
        },
      ],
    },
    {
      id: 'canary-pick',
      title: '카나리 화면 정하기',
      blocks: [
        {
          kind: 'steps',
          title: '따라 하기',
          steps: [
            { title: '[[화면]] 목록에서 먼저 받아 볼 화면 2~3대를 체크합니다.', body: '문제가 생겨도 영향이 적고, 직원이 가까이 있어 확인하기 쉬운 화면이 좋습니다.' },
            { title: '막대의 [[기타 일괄 변경]] 을 누르고 [[업데이트 채널…]] 에서 카나리를 고릅니다.', body: '선택하는 즉시 적용되며, 목록의 버전 옆에 “canary” 가 붙습니다.' },
          ],
        },
      ],
    },
    {
      id: 'pin',
      title: '버전 고정',
      blocks: [
        {
          kind: 'text',
          text: '화면 설정 창 [[소프트웨어]] 맨 아래 [[고급]] 의 **버전 고정**으로 화면 한 대를 특정 버전에 묶어 둘 수 있습니다. 고정한 화면은 새 버전이 배포되어도 갱신되지 않습니다(고정이 채널보다 우선합니다). 해제하려면 “고정 안 함” 을 고릅니다.',
        },
      ],
    },
    {
      id: 'stuck',
      title: '업데이트되지 않을 때',
      blocks: [
        {
          kind: 'steps',
          title: '위에서부터 차례로 확인하세요',
          steps: [
            { title: '화면이 켜져 있고 온라인(초록 점)인지 확인합니다.' },
            { title: '화면 목록에 “v… 고정” 표시가 붙어 있는지 확인합니다.', body: '붙어 있으면 화면 상세 “업데이트” 에서 고정을 해제합니다.' },
            { title: '그 화면의 채널(카나리/안정)로 새 버전이 배포되었는지 확인합니다.', body: '화면 목록에서 채널을 보고, 그 채널로 새 버전을 내보냈는지 공급사에 확인합니다.' },
            { title: '현장에서 정비 모드를 열고 [[지금 확인]] → [[지금 업데이트]] 를 누릅니다.', body: '안드로이드에서 설치 확인 창이 표시되면 “설치” 를 누릅니다. 정비 모드의 [[자동 업데이트]] 가 켜져 있는지도 확인합니다.' },
            { title: '마지막으로 오른쪽 위 **×** 를 누릅니다.' },
          ],
        },
        {
          kind: 'callout',
          tone: 'note',
          title: '웹 브라우저로 재생하는 화면은 자동으로 업데이트되지 않습니다',
          text: '브라우저를 새로고침하면 최신 버전으로 갱신됩니다.',
        },
      ],
    },
  ],
};
